import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  BookOpen,
  CheckCircle2,
  Clock3,
  Flame,
  GraduationCap,
  Megaphone,
  Play,
  RefreshCw,
  Search,
  Target,
  Trophy,
  Video,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Dashboard.css';

const badgeIcons = {
  'First Steps': GraduationCap,
  'Quick Learner': Flame,
  'Course Champion': Trophy,
  'Quiz Master': Target,
  'Dedicated Student': Award,
  Scholar: BookOpen,
};

const activityLabels = {
  course_enrolled: 'Enrolled in a course',
  course_completed: 'Completed a course',
  lesson_completed: 'Completed a lesson',
  video_completed: 'Completed a video',
  quiz_completed: 'Completed a quiz',
  assignment_submitted: 'Submitted an assignment',
  login: 'Signed in to learn',
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [myCourses, setMyCourses] = useState([]);
  const [badges, setBadges] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loginStreak, setLoginStreak] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [statsRes, coursesRes, badgesRes, streakRes, announcementsRes, analyticsRes] =
        await Promise.all([
          api.get('/progress/stats'),
          api.get('/enrollments/my-courses'),
          api.get('/badges/my-badges').catch(() => ({ data: [] })),
          api.get('/auth/login-streak').catch(() => ({ data: { streak: 0 } })),
          api.get('/announcements/my-announcements').catch(() => ({ data: [] })),
          api.get('/analytics/student').catch(() => ({ data: null })),
        ]);

      setStats(statsRes.data || {});
      setMyCourses(Array.isArray(coursesRes.data) ? coursesRes.data : []);
      setBadges(Array.isArray(badgesRes.data) ? badgesRes.data.slice(0, 4) : []);
      setLoginStreak(
        Number(streakRes.data?.streak) ||
          Number(analyticsRes.data?.streak?.current_streak) ||
          0
      );
      setAnnouncements(
        Array.isArray(announcementsRes.data) ? announcementsRes.data.slice(0, 4) : []
      );
      setRecentActivity(
        Array.isArray(analyticsRes.data?.recent_activity)
          ? analyticsRes.data.recent_activity.slice(0, 5)
          : []
      );
    } catch (requestError) {
      console.error('Error fetching dashboard data:', requestError);
      setError('We could not load your learning data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const enrolledCount = Number(stats?.enrolled_courses) || 0;
  const completedCount = Number(stats?.completed_courses) || 0;
  const videosCompleted = Number(stats?.videos_completed) || 0;
  const totalWatchSeconds = Number(stats?.total_watch_time) || 0;
  const inProgressCount = Math.max(enrolledCount - completedCount, 0);
  const completionPercentage = enrolledCount
    ? Math.min(Math.round((completedCount / enrolledCount) * 100), 100)
    : 0;

  const nextCourse = useMemo(
    () => myCourses.find((course) => !course.completed_at) || myCourses[0],
    [myCourses]
  );

  const categoryProgress = useMemo(() => {
    const categories = myCourses.reduce((result, course) => {
      const category = course.category_name || 'Other';
      const current = result[category] || { completed: 0, total: 0 };
      current.completed += Number(course.completed_videos) || 0;
      current.total += Number(course.total_videos) || 0;
      result[category] = current;
      return result;
    }, {});

    return Object.entries(categories)
      .map(([name, values]) => ({
        name,
        percentage: values.total
          ? Math.min(Math.round((values.completed / values.total) * 100), 100)
          : 0,
      }))
      .sort((a, b) => b.percentage - a.percentage)
      .slice(0, 3);
  }, [myCourses]);

  const watchTime = formatWatchTime(totalWatchSeconds);
  const unreadAnnouncements = announcements.filter((announcement) => !announcement.is_read).length;
  const greeting = getGreeting();
  const today = new Intl.DateTimeFormat('en', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date());

  if (loading) {
    return (
      <main className="learner-dashboard" aria-busy="true">
        <div className="learner-container dashboard-loading">
          <div className="skeleton skeleton-heading" />
          <div className="skeleton skeleton-feature" />
          <div className="skeleton-grid">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="skeleton skeleton-stat" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="learner-dashboard">
        <div className="learner-container dashboard-error" role="alert">
          <RefreshCw size={28} aria-hidden="true" />
          <h1>Your dashboard needs another try</h1>
          <p>{error}</p>
          <button type="button" className="learner-button primary" onClick={loadDashboard}>
            <RefreshCw size={17} aria-hidden="true" />
            Reload dashboard
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="learner-dashboard">
      <div className="learner-container">
        <header className="dashboard-heading">
          <div>
            <p className="dashboard-date">{today}</p>
            <h1>
              {greeting}, {user?.first_name || 'learner'}
            </h1>
            <p className="dashboard-intro">
              {inProgressCount > 0
                ? `You have ${inProgressCount} active ${pluralize('course', inProgressCount)} ready to continue.`
                : 'Your learning space is ready when you are.'}
            </p>
          </div>
          <Link to="/courses" className="learner-button secondary">
            <Search size={17} aria-hidden="true" />
            Explore courses
          </Link>
        </header>

        <section className="dashboard-overview" aria-label="Learning overview">
          <article className={`resume-course ${nextCourse ? '' : 'empty'}`}>
            {nextCourse ? (
              <>
                <div className="resume-copy">
                  <p className="section-kicker">Continue learning</p>
                  <h2>{nextCourse.title}</h2>
                  <p className="resume-instructor">
                    {nextCourse.instructor_name || 'Anu Kingdom instructor'}
                  </p>
                  <div className="resume-progress-row">
                    <div
                      className="learner-progress-track dark"
                      role="progressbar"
                      aria-label={`${nextCourse.title} progress`}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      aria-valuenow={getCourseProgress(nextCourse)}
                    >
                      <span style={{ width: `${getCourseProgress(nextCourse)}%` }} />
                    </div>
                    <strong>{getCourseProgress(nextCourse)}%</strong>
                  </div>
                  <p className="resume-meta">
                    {Number(nextCourse.completed_videos) || 0} of{' '}
                    {Number(nextCourse.total_videos) || 0} lessons complete
                  </p>
                  <Link
                    to={`/courses/${nextCourse.course_id}`}
                    className="learner-button light"
                  >
                    <Play size={16} fill="currentColor" aria-hidden="true" />
                    Resume course
                  </Link>
                </div>
                <div className="resume-visual" aria-hidden="true">
                  {nextCourse.thumbnail_url ? (
                    <img src={nextCourse.thumbnail_url} alt="" />
                  ) : (
                    <span>{getCourseInitials(nextCourse.title)}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="resume-copy">
                <p className="section-kicker">Start your path</p>
                <h2>Choose your first course</h2>
                <p className="resume-instructor">
                  Explore the catalogue and build a learning plan that fits your goals.
                </p>
                <Link to="/courses" className="learner-button light">
                  Browse catalogue
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            )}
          </article>

          <aside className="completion-panel">
            <div className="panel-heading compact">
              <div>
                <p className="section-kicker">Overall progress</p>
                <h2>Your completion</h2>
              </div>
              <span className="completion-value">{completionPercentage}%</span>
            </div>
            <div
              className="overall-progress"
              role="progressbar"
              aria-label="Overall course completion"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={completionPercentage}
            >
              <span style={{ width: `${completionPercentage}%` }} />
            </div>
            <div className="completion-breakdown">
              <div>
                <span className="status-dot complete" />
                <span>Completed</span>
                <strong>{completedCount}</strong>
              </div>
              <div>
                <span className="status-dot active" />
                <span>In progress</span>
                <strong>{inProgressCount}</strong>
              </div>
            </div>
            <Link to="/goals" className="text-link">
              Review learning goals
              <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </aside>
        </section>

        <section className="learning-stats" aria-label="Learning statistics">
          <StatItem
            icon={BookOpen}
            tone="green"
            label="Enrolled courses"
            value={enrolledCount}
            detail={`${inProgressCount} currently active`}
          />
          <StatItem
            icon={CheckCircle2}
            tone="teal"
            label="Courses completed"
            value={completedCount}
            detail="Across your learning history"
          />
          <StatItem
            icon={Video}
            tone="blue"
            label="Lessons completed"
            value={videosCompleted}
            detail="Recorded by course progress"
          />
          <StatItem
            icon={Clock3}
            tone="amber"
            label="Learning time"
            value={watchTime}
            detail="Total tracked watch time"
          />
        </section>

        <div className="dashboard-content-grid">
          <div className="dashboard-primary-column">
            <section className="dashboard-panel">
              <div className="panel-heading">
                <div>
                  <p className="section-kicker">My learning</p>
                  <h2>Course progress</h2>
                </div>
                <Link to="/my-courses" className="text-link">
                  View all
                  <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>

              {myCourses.length > 0 ? (
                <div className="course-progress-list">
                  {myCourses.slice(0, 4).map((course) => {
                    const progress = getCourseProgress(course);
                    return (
                      <article key={course.id} className="course-progress-row">
                        <div className="course-cover">
                          {course.thumbnail_url ? (
                            <img src={course.thumbnail_url} alt="" />
                          ) : (
                            <span>{getCourseInitials(course.title)}</span>
                          )}
                        </div>
                        <div className="course-row-content">
                          <div className="course-row-heading">
                            <div>
                              <p className="course-category">
                                {course.category_name || 'Course'}
                              </p>
                              <h3>{course.title}</h3>
                            </div>
                            {course.completed_at && (
                              <span className="course-status">
                                <CheckCircle2 size={13} aria-hidden="true" />
                                Complete
                              </span>
                            )}
                          </div>
                          <div className="course-row-progress">
                            <div
                              className="learner-progress-track"
                              role="progressbar"
                              aria-label={`${course.title} progress`}
                              aria-valuemin="0"
                              aria-valuemax="100"
                              aria-valuenow={progress}
                            >
                              <span style={{ width: `${progress}%` }} />
                            </div>
                            <strong>{progress}%</strong>
                          </div>
                          <div className="course-row-footer">
                            <span>
                              {Number(course.completed_videos) || 0} /{' '}
                              {Number(course.total_videos) || 0} lessons
                            </span>
                            <Link to={`/courses/${course.course_id}`}>
                              {course.completed_at ? 'Review' : 'Continue'}
                              <ArrowRight size={14} aria-hidden="true" />
                            </Link>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <EmptyPanel
                  icon={BookOpen}
                  title="No courses yet"
                  body="Find a course that matches your next goal and it will appear here."
                  linkTo="/courses"
                  linkLabel="Browse courses"
                />
              )}
            </section>

            <section className="dashboard-panel">
              <div className="panel-heading">
                <div>
                  <p className="section-kicker">Recent activity</p>
                  <h2>Your learning history</h2>
                </div>
                <Link to="/analytics" className="text-link">
                  Open analytics
                  <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>

              {recentActivity.length > 0 ? (
                <div className="activity-list">
                  {recentActivity.map((activity, index) => (
                    <div
                      key={`${activity.created_at || 'activity'}-${index}`}
                      className="activity-row"
                    >
                      <span className="activity-icon">
                        <CheckCircle2 size={16} aria-hidden="true" />
                      </span>
                      <div>
                        <p>{formatActivity(activity)}</p>
                        <span>{formatRelativeDate(activity.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyPanel
                  icon={Clock3}
                  title="No recent activity"
                  body="Completed lessons and course milestones will be recorded here."
                />
              )}
            </section>
          </div>

          <aside className="dashboard-secondary-column">
            <section className="dashboard-panel compact-panel">
              <div className="panel-heading">
                <div>
                  <p className="section-kicker">Momentum</p>
                  <h2>Learning streak</h2>
                </div>
                <span className="streak-icon">
                  <Flame size={20} aria-hidden="true" />
                </span>
              </div>
              <div className="streak-value">
                <strong>{loginStreak}</strong>
                <span>{pluralize('day', loginStreak)} active</span>
              </div>
              <p className="panel-note">Small, consistent sessions create lasting progress.</p>
            </section>

            <section className="dashboard-panel compact-panel">
              <div className="panel-heading">
                <div>
                  <p className="section-kicker">Focus areas</p>
                  <h2>By category</h2>
                </div>
              </div>
              {categoryProgress.length > 0 ? (
                <div className="category-progress-list">
                  {categoryProgress.map((category) => (
                    <div key={category.name} className="category-progress-row">
                      <div>
                        <span>{category.name}</span>
                        <strong>{category.percentage}%</strong>
                      </div>
                      <div
                        className="learner-progress-track slim"
                        role="progressbar"
                        aria-label={`${category.name} category progress`}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={category.percentage}
                      >
                        <span style={{ width: `${category.percentage}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="panel-note">Category progress appears after enrollment.</p>
              )}
            </section>

            <section className="dashboard-panel compact-panel">
              <div className="panel-heading">
                <div>
                  <p className="section-kicker">Updates</p>
                  <h2>Announcements</h2>
                </div>
                {unreadAnnouncements > 0 && (
                  <span className="count-badge">{unreadAnnouncements} new</span>
                )}
              </div>
              {announcements.length > 0 ? (
                <div className="announcement-list">
                  {announcements.map((announcement) => (
                    <article key={announcement.id} className="announcement-row">
                      <span className={`announcement-mark ${announcement.announcement_type || ''}`}>
                        <Megaphone size={15} aria-hidden="true" />
                      </span>
                      <div>
                        <Link
                          to={
                            announcement.course_id
                              ? `/courses/${announcement.course_id}/announcements`
                              : '/announcements'
                          }
                        >
                          {announcement.title}
                        </Link>
                        <p>
                          {announcement.course_title || 'Platform update'}
                          {announcement.published_at
                            ? ` - ${formatRelativeDate(announcement.published_at)}`
                            : ''}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="panel-note">You are all caught up. New course updates appear here.</p>
              )}
              <Link to="/announcements" className="text-link panel-footer-link">
                View announcements
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </section>

            <section className="dashboard-panel compact-panel">
              <div className="panel-heading">
                <div>
                  <p className="section-kicker">Recognition</p>
                  <h2>Recent badges</h2>
                </div>
                <Link to="/badges" className="text-link">
                  View all
                </Link>
              </div>
              {badges.length > 0 ? (
                <div className="badge-list">
                  {badges.map((badge) => {
                    const BadgeIcon = badgeIcons[badge.name] || Award;
                    return (
                      <div key={badge.id} className="badge-row">
                        <span>
                          <BadgeIcon size={17} aria-hidden="true" />
                        </span>
                        <div>
                          <strong>{badge.name}</strong>
                          <p>{badge.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="panel-note">Your first badge is waiting for your next milestone.</p>
              )}
            </section>
          </aside>
        </div>
      </div>
    </main>
  );
};

const StatItem = ({ icon: Icon, tone, label, value, detail }) => (
  <article className="learning-stat-item">
    <span className={`learning-stat-icon ${tone}`}>
      <Icon size={19} aria-hidden="true" />
    </span>
    <div>
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{detail}</span>
    </div>
  </article>
);

const EmptyPanel = ({ icon: Icon, title, body, linkTo, linkLabel }) => (
  <div className="dashboard-empty-state">
    <span>
      <Icon size={20} aria-hidden="true" />
    </span>
    <div>
      <h3>{title}</h3>
      <p>{body}</p>
      {linkTo && (
        <Link to={linkTo} className="text-link">
          {linkLabel}
          <ArrowRight size={15} aria-hidden="true" />
        </Link>
      )}
    </div>
  </div>
);

const getCourseProgress = (course) => {
  const total = Number(course?.total_videos) || 0;
  const completed = Number(course?.completed_videos) || 0;
  return total ? Math.min(Math.round((completed / total) * 100), 100) : 0;
};

const getCourseInitials = (value = '') =>
  value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

const formatWatchTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const formatActivity = (activity) => {
  const label = activityLabels[activity.activity_type] || 'Learning activity recorded';
  const data = activity.activity_data;

  if (data && typeof data === 'object') {
    const subject = data.course_title || data.lesson_title || data.quiz_title || data.title;
    return subject ? `${label}: ${subject}` : label;
  }

  return label;
};

const formatRelativeDate = (value) => {
  if (!value) return 'Recently';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';

  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date);
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const pluralize = (word, count) => (count === 1 ? word : `${word}s`);

export default Dashboard;
